import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, UserPlus, X } from "lucide-react";
import { API } from "../../App";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

export const LeadSheetAdminDialog = ({ item, token, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const fileRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  const base = item ? `${API}/admin/content/${item.content_id}/lead-sheet` : "";

  const apply = (d) => {
    setDetail(d);
    if (!d.sheet) { setThumb(null); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}${d.sheet.thumb_url}`, { headers, responseType: "blob" })
      .then((r) => setThumb(URL.createObjectURL(r.data))).catch(() => setThumb(null));
  };

  useEffect(() => {
    if (!item) return;
    setDetail(null); setThumb(null); setEmail("");
    axios.get(base, { headers }).then((r) => apply(r.data)).catch(() => toast.error("Could not load Lead Sheet"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.content_id]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      apply(r.data);
      toast.success(ok);
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => axios.post(base, fd, { headers }), "Lead Sheet uploaded");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="lead-sheet-admin-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">Lead Sheetz™: {item?.song_title || item?.title}</DialogTitle>
          <DialogDescription>Students only see this Lead Sheet once it's unlocked for them.</DialogDescription>
        </DialogHeader>
        {!detail ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-gold" /> : (
          <div className="space-y-5">
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="lead-sheet-admin-file-input" />
            {detail.sheet ? (
              <div className="space-y-2">
                {thumb && <img src={thumb} alt="Lead Sheet preview" className="max-h-72 rounded-lg border" data-testid="lead-sheet-admin-thumb" />}
                <p className="text-xs text-muted-foreground">{detail.sheet.filename} · {detail.downloads} downloads</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="lead-sheet-admin-replace-btn"><Upload className="mr-1.5 h-4 w-4" /> Replace PDF</Button>
                  <Button size="sm" variant="outline" className="text-destructive" disabled={busy} onClick={() => window.confirm("Remove this Lead Sheet? Students who unlocked it will no longer see it.") && run(() => axios.delete(base, { headers }), "Lead Sheet removed")} data-testid="lead-sheet-admin-remove-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Remove</Button>
                </div>
              </div>
            ) : (
              <Button disabled={busy} onClick={() => fileRef.current?.click()} data-testid="lead-sheet-admin-upload-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Upload Lead Sheet PDF
              </Button>
            )}

            {detail.sheet && (
              <div className="space-y-2 border-t pt-4">
                <p className="text-sm font-semibold">Unlock for a student</p>
                <div className="flex gap-2">
                  <Input placeholder="student@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-sheet-admin-unlock-email" />
                  <Button disabled={busy || !email.trim()} onClick={() => run(() => axios.post(`${base}/unlock`, { email }, { headers }), `Unlocked for ${email}`).then((ok) => ok && setEmail(""))} data-testid="lead-sheet-admin-unlock-btn"><UserPlus className="h-4 w-4" /></Button>
                </div>
                <ul className="space-y-1 text-sm" data-testid="lead-sheet-admin-unlock-list">
                  {detail.unlocks.length === 0 && <li className="text-xs text-muted-foreground">Not unlocked for anyone yet. Admins can always see it.</li>}
                  {detail.unlocks.map((u) => (
                    <li key={u.user_id} className="flex items-center justify-between rounded border px-2 py-1">
                      <span className="truncate">{u.email} <span className="text-xs text-muted-foreground">({u.source})</span></span>
                      <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => run(() => axios.delete(`${base}/unlock/${u.user_id}`, { headers }), "Access removed")} aria-label={`Remove access for ${u.email}`} data-testid={`lead-sheet-admin-revoke-${u.user_id}`}><X className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
